import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as Tableau from 'tableau-api-js';
import FilterSelectDropdown from '../components/FilterSelectDropdown';
import Icon from '../components/Icon';
import ProjectSection from '../components/ProjectSection';
import TableauCharts from '../components/TableauCharts';
import useAnalyticsFilters from '../hooks/useAnalyticsFilters';
import { COLORS } from '../styles/colors';
import Page from '../templates/Page';
import { exportInitiativesCsv } from '../utils/csvExport';
import axios from 'axios';
import { ITEMS_PER_PAGE, NUMBER_ITEMS, PAGES, PROD_ENDPOINTS, REVIEW_ENDPOINTS } from '../constants/api';
import { FILTER_HEADING } from '../constants/uiText';
import { Link } from 'react-router-dom';
import { DEFAULT_SORT_OPTIONS } from '../utils/sortOptions';
import { TABLEAU_DISPLAY_NAME } from '../constants/tableauConstants';

const HomePage = () => {
  const tableauContainerRef = useRef<HTMLDivElement>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { t } = useTranslation();

  const viz = useRef<any>();
  const sheet = useRef<any>();
  const activeSheet = useRef<any>();

  const mapSheet = 2;

  const [activeDropdown, setActiveDropdown] =
    useState<string>(null);

  const [advancedFiltersOpen, setAdvancedFiltersOpen] =
    useState(false);

  const [exporting, setExporting] = useState(false);

  const {
    loading,
    initiatives,
    countryCount,
    initiativesCount,
    page,
    setPage,
    pages,
    filters,
    selectedFilters,
    setSelectedFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    areFiltersEmpty,
    sort,
    setSort,
  } = useAnalyticsFilters();

  useEffect(() => {
    const url =
      'https://tableau.apps.fao.org/views/AgriTO20-1-2026/AgriTODashboard?:origin=card_share_link&:embed=n';

    const options = {
      hideTabs: true,
      hideToolbar: true,
      width: '100%',
      height: 991,
      onFirstInteractive: () => {
        sheet.current = viz.current
          .getWorkbook()
          .getActiveSheet();
        activeSheet.current = viz.current
          .getWorkbook()
          .getActiveSheet()
          .getWorksheets()[mapSheet];
        viz.current.addEventListener(
          window.tableau.TableauEventName.MARKS_SELECTION,
          onMarksSelection
        );
      },
    };

    viz.current = new Tableau.Viz(
      tableauContainerRef.current,
      url,
      options
    );

    return () =>
      viz.current.removeEventListener(
        window.tableau.TableauEventName.MARKS_SELECTION,
        onMarksSelection
      );
  }, []);

  function onMarksSelection(marksEvent) {
    return marksEvent
      .getMarksAsync()
      .then(reportSelectedMarks);
  }

  function reportSelectedMarks(marks) {
    for (
      var markIndex = 0;
      markIndex < marks.length;
      markIndex++
    ) {
      var pairs = marks[markIndex].getPairs();

      for (
        var pairIndex = 0;
        pairIndex < pairs.length;
        pairIndex++
      ) {
        var pair = pairs[pairIndex];
        /* console.log("PARAMS", pair.fieldName, pair.formattedValue); */
        switch (pair.fieldName) {
          case 'Country or territory':
            setFilterData({
              country: [pair.formattedValue],
            });
            break;
          case 'Usecase_filter':
            setFilterData({
              use_case: [pair.formattedValue],
            });
            break;
          case 'Theme_filter':
            setFilterData({ theme: [pair.formattedValue] });
            break;
          case 'Technology_filter':
            setFilterData({
              technology: [pair.formattedValue],
            });
            break;
          default:
            break;
        }
      }
    }
  }

  const setFilterData = (data) => {
    setSelectedFilters((state) => {
      const newData = { ...state, ...data };
      return newData;
    });
  };

  // On change Search (group)
  useEffect(() => {
    if (sheet.current) {
      sheet.current.applyFilterAsync(
        'Search',
        searchTerm,
        window.tableau.FilterUpdateType.REPLACE
      );
    }
  }, [searchTerm]);

  const filterList: {
    [key: string]: { enableSearch?: boolean };
  } = {
    type: {},
    country: { enableSearch: true },
    use_case: {},
    theme: { enableSearch: true },
    technology: { enableSearch: true },
  };

  const advancedFilterList: {
    [key: string]: { enableSearch?: boolean };
  } = {
    business_model: {},
    maturity: {},
    fvc: {},
    users: { enableSearch: true },
    organization_type: { enableSearch: true },
    origin_country: { enableSearch: true },
    sdg: { enableSearch: true },
    four_betters: {},
  };

  const applyFilter = useCallback(
    (key: string, filterName: string): void => {
      console.log(key);
      if (sheet.current) {
        if (selectedFilters[key].length === 0) {
          sheet.current.applyFilterAsync(
            filterName,
            '',
            window.tableau.FilterUpdateType.ALL
          );
        } else {
          sheet.current.applyFilterAsync(
            filterName,
            [...selectedFilters[key]],
            window.tableau.FilterUpdateType.REPLACE
          );
        }
      }
    },
    [filters, selectedFilters]
  );

  useEffect(
    () => applyFilter('business_model', TABLEAU_DISPLAY_NAME.BUSINESS_MODEL),
    [selectedFilters.business_model]
  );
  useEffect(
    () => applyFilter('country', TABLEAU_DISPLAY_NAME.COUNTRY_OR_TERRITORY),
    [selectedFilters.country]
  );
  useEffect(
    () => applyFilter('four_betters', TABLEAU_DISPLAY_NAME.FOUR_BETTERS),
    [selectedFilters.four_betters]
  );
  useEffect(
    () => applyFilter('maturity', TABLEAU_DISPLAY_NAME.MATURITY),
    [selectedFilters.maturity]
  );
  useEffect(
    () => applyFilter('sdg', TABLEAU_DISPLAY_NAME.SGDS),
    [selectedFilters.sdg]
  );
  useEffect(
    () => applyFilter('technology', TABLEAU_DISPLAY_NAME.TECHNOLOGY),
    [selectedFilters.technology]
  );
  useEffect(
    () => applyFilter('theme', TABLEAU_DISPLAY_NAME.THEME),
    [selectedFilters.theme]
  );
  useEffect(
    () => applyFilter('type', TABLEAU_DISPLAY_NAME.TYPE),
    [selectedFilters.type]
  );
  useEffect(
    () => applyFilter('fvc', TABLEAU_DISPLAY_NAME.FOOD_VALUE_CHAIN),
    [selectedFilters.fvc]
  );
  useEffect(
    () => applyFilter('use_case', TABLEAU_DISPLAY_NAME.USE_CASE),
    [selectedFilters.use_case]
  );
  useEffect(
    () => applyFilter('users', TABLEAU_DISPLAY_NAME.USERS),
    [selectedFilters.users]
  );
  useEffect(
    () =>
      applyFilter('organization_type', TABLEAU_DISPLAY_NAME.ORGANIZATION_TYPE),
    [selectedFilters.organization_type]
  );
  useEffect(
    () =>
      applyFilter('origin_country', TABLEAU_DISPLAY_NAME.COUNTRY_OF_ORIGIN),
    [selectedFilters.origin_country]
  );

  /**
   * Bootstrap tooltips are JavaScript-driven components and are NOT activated
   * automatically by adding `data-bs-toggle="tooltip"` in the markup.
   * Each element declaring itself as a tooltip trigger must be explicitly
   * initialized via `new bootstrap.Tooltip(...)`.
   *
   * A classic for-loop is used instead of iterable syntax to ensure
   * compatibility with projects targeting ES5.
   */
  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    for (let i = 0; i < tooltipTriggerList.length; i++) {
      const el = tooltipTriggerList[i] as HTMLElement;
      new (window as any).bootstrap.Tooltip(el);
    }
  }, []);

  const exportCurrentFilteredCsv = async () => {
    if (exporting) return;
    setExporting(true);
    
    try {
      const response = await axios.post(
        PROD_ENDPOINTS.CASCADE_FILTER,
        {
          numberItems: ITEMS_PER_PAGE,
          page,
          search: searchTerm,
          sort,
          type: selectedFilters.type,
          countries: selectedFilters.country,
          useCase: selectedFilters.use_case,
          technology: selectedFilters.technology,
          theme: selectedFilters.theme,
          users: selectedFilters.users,
          businessModel: selectedFilters.business_model,
          fvc: selectedFilters.fvc,
          maturity: selectedFilters.maturity,
          organizationType: selectedFilters.organization_type,
          fourBetters: selectedFilters.four_betters,
          sdg: selectedFilters.sdg,
          countryOfOrigin: selectedFilters.origin_country,
          export: true,
        }
      );

      if (response.status === 200) {
        const { data } = response.data;
        exportInitiativesCsv({ 
          dataSource: data, 
          selectedFilters, 
          searchTerm, 
          prefix: 'agrito', 
        });
      }
    } catch (error) {
      console.error('Error failed', error);
    } finally {
      setExporting(false);
    }
  };

  const filtersHelpPopover = (
  <Popover id="filters-help-popover" style={{ maxWidth: 320 }}>
    <Popover.Body style={{ fontSize: '0.95rem', lineHeight: 1.4 }}>
      <p className='mb-2'>
        When you choose multiple options within one filter, results are broadened—the entry can match any of the selected items.
      </p>
      <p className='mb-2'>
        When you choose options from different filters, results are refined—the entry must satisfy each filter, while still matching any value inside each one.
      </p>
      <p className='mb-2'>
        Example: If you pick Albania and Spain, entries from either country will appear. If you also pick SDG 7 and SDG 15, entries shown must be from Albania or Spain and must address SDG 7 or SDG 15.
      </p>
      <p className='mb-0'>
        To learn what each filter means see the{' '}
        <Link
          to="/taxonomy"
          style={{
            color: COLORS.Primary,
            textDecoration: 'underline',
            fontWeight: 500,
          }}
        >
          Taxonomy
        </Link>
        .
      </p>
    </Popover.Body>
  </Popover>
);




  // const exportCurrentFilteredCsv = async () => {
  //   if (exporting) return;
  //   setExporting(true);
    
  //   try {
  //     const response = await axios.post(
  //       CASCADE_FILTER_ENDPOINT,
  //       {
  //         numberItems: NUMBER_ITEMS,
  //         page: PAGES,
  //         search: searchTerm,

  //         type: selectedFilters.type,
  //         countries: selectedFilters.country,
  //         useCase: selectedFilters.use_case,
  //         technology: selectedFilters.technology,
  //         theme: selectedFilters.theme,
  //         users: selectedFilters.users,
  //         businessModel: selectedFilters.business_model,
  //         fvc: selectedFilters.fvc,
  //         maturity: selectedFilters.maturity,
  //         organizationType: selectedFilters.organization_type,
  //         fourBetters: selectedFilters.four_betters,
  //         sdg: selectedFilters.sdg,
  //         countryOfOrigin: selectedFilters.origin_country,
  //         export: true,
  //       }
  //     );

  //     if (response.status === 200) {
  //       const { data } = response.data;
  //       exportInitiativesCsv({ 
  //         dataSource: data, 
  //         selectedFilters, 
  //         searchTerm, 
  //         prefix: 'agrito', 
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error failed', error);
  //   } finally {
  //     setExporting(false);
  //   }
  // };


  return (
    <Page
      title="AgriTech Observatory"
      subtitle="Navigate digital agriculture developments in Europe and Central Asia​"
      actions={
        <Button className="d-block d-sm-none">
          <Icon
            icon={isMenuOpen ? 'close' : 'filter'}
            onClick={() => setIsMenuOpen((state) => !state)}
          />
        </Button>
      }
    >
      <div
        role="region"
        aria-label="Zoom interview invitation bootstrap comparison"
        className="card mb-5 rounded-4 border bg-primary-light"
        style={{ borderColor: 'var(--primary)' }}
      >
        <div className="card-body p-3 p-sm-4">
          <p className="mb-2 text-uppercase fw-bold text-color-primary">
            Invitation
          </p>
          <h4 className="h4 mb-2 text-color-primary">
            Help shape the future of the AgriTech Observatory
          </h4>
          <p className="mb-3 text-color-default">
            Your experience using the Observatory matters.
            We&apos;re conducting short Zoom interviews (20
            min) to learn how you work with agricultural
            technology data and where we can do better.
          </p>
          <p className="mb-0 text-color-default">
            Interested to help us? Fill out{' '}
            <a
              href="https://forms.office.com/Pages/ResponsePage.aspx?id=aMQ6Frir0ESB_dnbFeOvlq9DXHtJLzNLmhNhuNmxnAJURDRFVlQwQ0U3STVRWDhTNlAxUVRFUDVJWS4u"
              target="_blank"
              rel="noopener noreferrer"
              className="link-primary"
            >
              this brief form
            </a>{' '}
            and we&apos;ll be in touch.
          </p>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <TableauDashboardContent>
          <TableauDashboardFilters $menuOpen={isMenuOpen}>
            <div
              style={{
                borderTop: `1px solid ${COLORS.Border}`,
                borderBottom: `1px solid ${COLORS.Border}`,
                padding: '0.5rem 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h1 style={{ fontSize: '2rem', fontWeight: '500', margin: 0, }} >
                {FILTER_HEADING}
              </h1>
              <OverlayTrigger trigger={['focus', 'click']} placement="right" overlay={filtersHelpPopover} rootClose >
                <button
                  type="button"
                  aria-label="Explain filtering logic"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 32,
                    width: 32,
                    cursor: 'pointer',
                    color: COLORS.Primary,
                  }}
                >
                  <i className="bi bi-info-circle" style={{ fontSize: 18 }} />
                </button>
              </OverlayTrigger>
            </div>
            {Object.entries(filterList).map(
              ([id, data]) => (
                <FilterSelectDropdown
                  key={id}
                  id={id}
                  label={t(`home.filters.${id}.label`)}
                  placeholder={t(
                    `home.filters.${id}.placeholder`
                  )}
                  list={filters[id]}
                  activeList={selectedFilters[id]}
                  setState={setFilterData}
                  disabled={loading}
                  activeDropdown={activeDropdown}
                  setActiveDropdown={setActiveDropdown}
                  enableSearch={data.enableSearch}
                />
              )
            )}
            {advancedFiltersOpen &&
              Object.entries(advancedFilterList).map(
                ([id, data]) => (
                  <FilterSelectDropdown
                    key={id}
                    id={id}
                    label={t(`home.filters.${id}.label`)}
                    placeholder={t(
                      `home.filters.${id}.placeholder`
                    )}
                    list={filters[id]}
                    activeList={selectedFilters[id]}
                    setState={setFilterData}
                    disabled={loading}
                    activeDropdown={activeDropdown}
                    setActiveDropdown={setActiveDropdown}
                    enableSearch={data.enableSearch}
                  />
                )
              )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {!advancedFiltersOpen ? (
                <InteractiveButton
                  onClick={() =>
                    setAdvancedFiltersOpen(true)
                  }
                >
                  Show advanced filters
                </InteractiveButton>
              ) : (
                <InteractiveButton
                  onClick={() =>
                    setAdvancedFiltersOpen(false)
                  }
                >
                  Hide advanced filters
                </InteractiveButton>
              )}
              {!areFiltersEmpty && (
                <InteractiveButton onClick={resetFilters}>
                  Reset filters
                </InteractiveButton>
              )}
            </div>
            <div
              style={{
                borderTop: `1px solid ${COLORS.Border}`,
                borderBottom: `1px solid ${COLORS.Border}`,
                padding: '0.5rem 0',
                marginTop: '2rem',
              }}
            >
              <h1
                style={{
                  fontSize: '2rem',
                  fontWeight: '500',
                }}
              >
                Initiatives
              </h1>
              <a
                href="#initiatives"
                style={{ cursor: 'pointer' }}
              >
                List of initiatives
              </a>
            </div>
          </TableauDashboardFilters>
          <div
            style={{
              flex: 1,
              position: 'relative',
              top: '-10px',
            }}
          >
            {!loading ? (
              <>
                <h1
                  style={{
                    fontSize: 24,
                    fontWeight: 500,
                    color: COLORS.Black,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      color: COLORS.Primary,
                    }}
                  >
                    {initiativesCount}
                  </span>
                  {` initiatives in `}
                  <span
                    style={{
                      fontWeight: 700,
                      color: COLORS.Primary,
                    }}
                  >
                    {countryCount}
                  </span>
                  {` ${countryCount === 1 ? 'country' : 'countries'} or ${
                    countryCount === 1
                      ? 'territory'
                      : 'territories'
                  }`}
                </h1>
              </>
            ) : (
              <div>
                <h1
                  style={{
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'black',
                  }}
                >
                  Loading results...
                </h1>
              </div>
            )}
            <ResponsiveTableauContainer>
              <TableauCharts
                tableauContainerRef={tableauContainerRef}
                initiatives={initiatives}
              />
            </ResponsiveTableauContainer>
          </div>
        </TableauDashboardContent>
        <ProjectSection
          page={page}
          projects={!loading ? initiatives : []}
          pages={pages}
          setPage={setPage}
          loading={loading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onExportCsv={exportCurrentFilteredCsv}
          exportDisabled={loading || exporting || initiativesCount === 0}
          exporting={exporting}
          sortOptions={DEFAULT_SORT_OPTIONS}
          selectedSort={sort}
          onSortChange={(id) => setSort(id)}
        />
      </div>
    </Page>
  );
};

export default HomePage;

const TableauDashboardFilters = styled.div<{
  $menuOpen: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 250px;

  @media (max-width: 574px) {
    ${(props) => !props.$menuOpen && 'display: none;'}
  }
`;

const TableauDashboardContent = styled.div`
  width: 100%;
  display: flex;
  gap: 1rem;

  @media (max-width: 574px) {
    flex-direction: column;
  }
`;

const InteractiveButton = styled.p`
  color: rgb(17, 107, 172);
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ResponsiveTableauContainer = styled.div`
  display: none;

  @media (min-width: 640px) {
    display: block;
  }
`;
